import { CreditCard as CardIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

import usePaymentSourcesMutations from "features/payment-methods/datahooks/usePaymentSourcesMutations";
import type { PaymentSource } from "features/payment-methods/types";

import PoweredByStripe from "common/components/PoweredByStripe";
import { Button } from "common/components/ui/Button";
import { DialogDescription, DialogTitle } from "common/components/ui/Dialog";
import { DrawerDescription, DrawerTitle } from "common/components/ui/Drawer";
import { trackMetaPixel } from "common/helpers/utils";
import useGTM from "common/hooks/useGTM";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import CreditCard from "./CreditCard";
import PaypalCheckout from "./PaypalCheckout";

interface AddPaymentMethodProps {
  onBack: () => void;
  setPaymentSource: (newPaymentSource: PaymentSource) => void;
}

export default function AddPaymentMethod({
  onBack,
  setPaymentSource,
}: AddPaymentMethodProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [isAddingCard, setIsAddingCard] = useState(false);
  const {
    addPaymentSource,
    isAddingPaymentSource,
    isAddingPaymentSourceFromIntent,
  } = usePaymentSourcesMutations();

  const { sendEvent } = useGTM();

  useEffect(() => {
    let isCbInitialized: boolean;
    try {
      isCbInitialized = !!window.Chargebee.getInstance();
    } catch (_err) {
      isCbInitialized = false;
    }

    if (window.Chargebee && !isCbInitialized) {
      window.Chargebee.init({
        site: import.meta.env.VITE_CHARGEBEE_SITE,
        publishableKey: import.meta.env.VITE_CHARGEBEE_KEY,
      });
    }
  }, []);

  function onAddSuccess(paymentSource: PaymentSource) {
    sendEvent("add_payment_source_success", ({ email }) => ({
      email,
      payment_method: paymentSource.type,
    }));
    setPaymentSource(paymentSource);
    trackMetaPixel("track", "AddPaymentInfo");
  }
  async function addPaypalPaymentSource(token: string) {
    const source = "paypal";
    try {
      const paymentSource = await addPaymentSource({
        token,
        source,
      });
      onAddSuccess(paymentSource);
    } catch (_err) {
      sendEvent("add_payment_source_fail", ({ email }) => ({
        email,
        payment_method: source,
      }));
    }
  }

  const isLoading = isAddingPaymentSource || isAddingPaymentSourceFromIntent;

  let content = (
    <>
      <div className="flex flex-col gap-3 mb-3">
        <button
          className="flex w-full items-center justify-center gap-2 rounded-3xl bg-purple-50 py-4 text-sm-bold"
          onClick={() => setIsAddingCard(true)}
          type="button"
        >
          <CardIcon weight="fill" size={20} />
          Credit Card
        </button>

        <PaypalCheckout onTokenReceived={addPaypalPaymentSource} />
      </div>
      <PoweredByStripe />

      <Button
        onClick={onBack}
        variant="secondary-black"
        disabled={isLoading}
        className="mr-auto mt-10"
      >
        Back
      </Button>
    </>
  );

  if (isAddingCard) {
    content = (
      <CreditCard
        onSuccess={onAddSuccess}
        onBack={() => setIsAddingCard(false)}
      />
    );
  }

  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <>
      <Title>Add payment method</Title>
      <Description className="mb-8 ">
        Add a preferred payment method for your Aimfox subscription. You can
        change this at any time
      </Description>

      {content}
    </>
  );
}
